import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Grid, Header } from 'semantic-ui-react';

import { Message } from 'components';
import { arrangeSector } from 'utils/helpers';
import { ExposureTable, ExposureChart } from '../../Analytics/components';
import SectorBreakdown from '../../Analytics/components/SectorBreakdownTable';
import {
  translateBenchmarkDescription,
  translateText,
} from '../../../../utils/helpers';
import { translation } from '../../../Translation/scorecard';

@connect((state) => ({
  french: state.Storage.language === 'fr',
  data: state.Analytics.data,
  benchmark: state.Analytics.benchmark || 'domestic',
}))
export default class GlobalSector extends Component {
  getTableData = (data) => {
    const arranged = arrangeSector(data);
    const result = Object.keys(arranged).reduce((total, key) => {
      total.push({
        ticker: key,
        value: data[key],
      });

      return total;
    }, []);
    return result;
  };

  render() {
    const { user_region, toggle_region, french, data, benchmark } = this.props;
    const { sectorAllocation } = data;
    const portfolioSectors = sectorAllocation.portfolio;
    const benchmarkSectors = sectorAllocation[benchmark]; // use the benchmark chosen by the user
    const language = french ? 'fr' : 'en';

    return (
      <Container>
        <Header size="medium" dividing>
          {french
            ? "Analyse des titres et de l'exposition au risque"
            : 'Holdings and Risk Exposure Analysis'}
          <Header.Subheader>
            {french ? 'Par secteur boursier mondial' : 'By Global Stock Sector'}
          </Header.Subheader>
        </Header>

        {typeof data.sectBreakdown === 'object' && (
          <div>
            <Header
              size="medium"
              content={french ? 'Répartition des titres' : 'Holdings Breakdown'}
              subheader={
                french ? 'Sous-portefeuille actions' : 'Stock Sub-Portfolio'
              }
            />
            <p>
              {french
                ? "Cette répartition est le décompte pondéré par la valeur des titres boursiers dans chaque secteur boursier mondial au sein de votre sous-portefeuille actions (ou de votre portefeuille total si vous n'avez pas d'autre classe d'actifs)."
                : 'This breakdown is the value-weighted count of the stock holdings in each global stock sector within your stock sub-portfolio (or your total portfolio if you have no other asset class).'}
            </p>
            {sectorAllocation.msg.length > 0 ? (
              <Message visible warning className="msg">
                {translateText(sectorAllocation.msg, french)}
              </Message>
            ) : (
              <Grid>
                <Grid.Row columns={2}>
                  <Grid.Column>
                    <ExposureChart
                      title=""
                      labels={Object.keys(arrangeSector(data.sectBreakdown))}
                      data={Object.values(arrangeSector(data.sectBreakdown))}
                      hideLegend
                    />
                    <div style={{ marginTop: '5rem' }}>
                      * {translation[language].BENCHMARK} :{' '}
                      {translateBenchmarkDescription(
                        benchmarkSectors.desc,
                        french
                      )}
                      . <br />
                      ** {translation[language].OVERWEIGHT_TOOLTIP}
                    </div>
                  </Grid.Column>
                  <Grid.Column>
                    <SectorBreakdown
                      portfolio={arrangeSector(portfolioSectors)}
                      benchmark={arrangeSector(benchmarkSectors)}
                      pdf
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            )}
          </div>
        )}

        {data.riskExp && typeof data.riskExp['S.EQ'] === 'object' ? (
          <div>
            <Header
              size="medium"
              content={french ? 'Exposition au risque' : 'Risk Exposure'}
              subheader={
                french
                  ? "Sous-portefeuille actions (à l'exclusion des fonds équilibrés/de répartition d'actifs)"
                  : 'Stock Sub-Portfolio (excluding Balanced/Asset Allocation Funds)'
              }
            />
            <p>
              {french
                ? 'Ces expositions sont estimées par analyse statistique. Elles reflètent la volatilité et les effets de diversification de votre sous-portefeuille actions. Elles offrent une image différente de la répartition des titres, car cette dernière ne tient pas compte du fait que certaines entreprises peuvent couvrir leur risque sectoriel ou avoir des opérations dans plusieurs secteurs. Il est important de noter que certains risques sectoriels peuvent être réduits ou diversifiés si vous détenez plusieurs actifs dans votre portefeuille.'
                : 'These exposures are estimated using statistical analysis. They reflect the volatility and the diversification effects of your stock sub-portfolio. It provides a different picture from holdings breakdown, as the latter does not take into account the fact that some companies may hedge their sector risk, or have operations in more than one sector. Importantly, certain sector risks may be reduced or diversified away if you hold multiple assets in your portfolio.'}
            </p>
            <Grid>
              <Grid.Row columns={2}>
                <Grid.Column>
                  <ExposureChart
                    title=""
                    labels={Object.keys(arrangeSector(data.riskExp['S.EQ']))}
                    data={Object.values(arrangeSector(data.riskExp['S.EQ']))}
                    hideLegend
                  />
                </Grid.Column>
                <Grid.Column>
                  <ExposureTable
                    headers={[
                      <div>
                        {french
                          ? 'Secteur boursier mondial'
                          : 'Global Stock Sector'}
                      </div>,
                      <div>
                        {french
                          ? 'Exposition au risque sectoriel'
                          : 'Sector Risk Exposure'}
                      </div>,
                    ]}
                    totals={[french ? 'Total' : 'Total', <div>100%</div>]}
                    data={this.getTableData(data.riskExp['S.EQ'])}
                    pdf
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </div>
        ) : (
          <div style={{ margin: '2rem 0' }}>
            <Header
              size="medium"
              content={french ? 'Exposition au risque' : 'Risk Exposure'}
              subheader={
                french
                  ? "Sous-portefeuille actions (à l'exclusion des fonds équilibrés/de répartition d'actifs)"
                  : 'Stock Sub-Portfolio (excluding Balanced/Asset Allocation Funds)'
              }
            />
            <p>
              {french
                ? 'Ces expositions sont estimées par analyse statistique. Elles reflètent la volatilité et les effets de diversification de votre sous-portefeuille actions. Elles offrent une image différente de la répartition des titres, car cette dernière ne tient pas compte du fait que certaines entreprises peuvent couvrir leur risque sectoriel ou avoir des opérations dans plusieurs secteurs. Il est important de noter que certains risques sectoriels peuvent être réduits ou diversifiés si vous détenez plusieurs actifs dans votre portefeuille.'
                : 'These exposures are estimated using statistical analysis. They reflect the volatility and the diversification effects of your stock sub-portfolio. It provides a different picture from holdings breakdown, as the latter does not take into account the fact that some companies may hedge their sector risk, or have operations in more than one sector. Importantly, certain sector risks may be reduced or diversified away if you hold multiple assets in your portfolio.'}
            </p>
            <Message visible warning>
              {translateText(data.riskExp['S.EQ'], french)}
            </Message>
          </div>
        )}

        {data.riskExp && typeof data.riskExp.S === 'object' ? (
          <div>
            <Header
              size="medium"
              content={french ? 'Exposition au risque' : 'Risk Exposure'}
              subheader={french ? 'Portefeuille total' : 'Total Portfolio'}
            />
            <p>
              {french
                ? "Ces expositions sont estimées par analyse statistique. Elles représentent la volatilité et l'effet de diversification de votre portefeuille total. Si vous avez des obligations ou des actifs alternatifs dans votre portefeuille, ces chiffres seront différents de ceux du tableau précédent, car des risques sectoriels supplémentaires peuvent être ajoutés ou diversifiés."
                : 'These exposures are estimated using statistical analysis. They represent the volatility and diversification effect of your total portfolio. If you have bonds or alternative assets in your portfolio, these figures will be different from those in the previous table, as additional global stock sector risk may be added or diversified away.'}
            </p>
            <Grid>
              <Grid.Row columns={2}>
                <Grid.Column>
                  <ExposureChart
                    title=""
                    labels={Object.keys(arrangeSector(data.riskExp.S))}
                    data={Object.values(arrangeSector(data.riskExp.S))}
                    hideLegend
                  />
                </Grid.Column>
                <Grid.Column>
                  <ExposureTable
                    headers={[
                      <div>
                        {french
                          ? 'Secteur boursier mondial'
                          : 'Global Stock Sector'}
                      </div>,
                      <div>
                        {french
                          ? 'Exposition au risque sectoriel'
                          : 'Sector Risk Exposure'}
                      </div>,
                    ]}
                    totals={[french ? 'Total' : 'Total', <div>100%</div>]}
                    data={this.getTableData(data.riskExp.S)}
                    pdf
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </div>
        ) : (
          <div>
            <Header
              size="medium"
              content={french ? 'Exposition au risque' : 'Risk Exposure'}
              subheader={french ? 'Portefeuille total' : 'Total Portfolio'}
            />
            <p>
              {french
                ? "Ces expositions sont estimées par analyse statistique. Elles représentent la volatilité et l'effet de diversification de votre portefeuille total. Si vous avez des obligations ou des actifs alternatifs dans votre portefeuille, ces chiffres seront différents de ceux du tableau précédent, car des risques sectoriels supplémentaires peuvent être ajoutés ou diversifiés."
                : 'These exposures are estimated using statistical analysis. They represent the volatility and diversification effect of your total portfolio. If you have bonds or alternative assets in your portfolio, these figures will be different from those in the previous table, as additional global stock sector risk may be added or diversified away.'}
            </p>
            <Message visible warning>
              {translateText(data.riskExp.S, french)}
            </Message>
          </div>
        )}
      </Container>
    );
  }
}

GlobalSector.propTypes = {
  user_region: PropTypes.string.isRequired,
  toggle_region: PropTypes.string.isRequired,
  french: PropTypes.bool,
  data: PropTypes.object.isRequired,
  benchmark: PropTypes.string.isRequired,
};

GlobalSector.defaultProps = {
  french: false,
};

const Container = styled.div`
  padding: 2rem 2rem 1rem 2rem;

  .msg {
    width: fit-content;
  }
`;
